import { FC, Ref, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getAuthTeamMember } from 'store/authentication/selectors';
import { DASHBOARD } from 'queries/query-keys';
import { useBuilderChat } from 'ts-components/PiChat/builder/useBuilderChat';
import { BuilderChatPanel } from 'ts-components/PiChat/builder/BuilderChatPanel';
import { useBlockSession } from './blockVersions';
import { DEFAULT_KICODE_URL } from 'ts-components/PiChat/connectionDefaults';
import {
  resolveConnectOptions,
  type AiBlockPanelContext,
} from './connectOptions';
import type { ToolItem } from 'ts-components/PiChat/PiChatLog';
import type { GuardBusy } from 'ts-components/PiChat/PiChatPanel';
import { ResumePendingPanel } from 'ts-components/KiCode/session/ResumePendingPanel';
import { useResumeConversationId } from 'ts-components/PiChat/kicodeSessions';
import { handleCaptureRequest } from 'ts-components/PiChat/capture/captureRequest';
import { BlockToolRow } from './BlockToolRow';
import { CopyPromptMenu } from './CopyPromptMenu';
import { captureDashletForRequest } from './captureDashlet';
import { blockResumeQuery, pickPanelConversationId } from './blockResume';

export type { AiBlockPanelContext };

const QA_PREFIX = 'ai-block-panel';
const UNGROUPED_TOOLS = new Set(['push_block', 'capture_block']);
const groupsBlockTool = (item: ToolItem) => !UNGROUPED_TOOLS.has(item.toolName);

export type AiBlockPanelProps = {
  context: AiBlockPanelContext;
  dashletId?: string;
  conversationId?: string;
  onClose: () => void;
  guardRef?: Ref<GuardBusy>;
  onBlockPushed?: (dashletId: string, conversationId: string) => void;
  blockName?: string | null;
  dashboardName?: string | null;
  objectName?: string;
  className?: string;
};

// The resume id resolves before the chat mounts so `session.start` carries it;
// only the ids the panel opened with are looked up (a later push owns the id).
export const AiBlockPanel: FC<AiBlockPanelProps> = (props) => {
  const employeeId: string | undefined = useSelector(getAuthTeamMember)?.id;
  const [opened] = useState(() => ({
    dashletId: props.dashletId,
    conversationId: props.conversationId,
  }));
  const resume = useResumeConversationId(
    blockResumeQuery(props.context.dashboardId, opened.dashletId, employeeId),
    opened.conversationId ?? null
  );
  // Starting the chat before the team member loads would remount it later.
  if (!employeeId || resume.pending) {
    return (
      <ResumePendingPanel
        qaPrefix={QA_PREFIX}
        onClose={props.onClose}
        className={props.className}
      />
    );
  }
  return (
    <AiBlockChat
      {...props}
      conversationId={pickPanelConversationId(
        opened,
        props,
        resume.conversationId
      )}
    />
  );
};

const AiBlockChat: FC<AiBlockPanelProps> = ({
  context,
  dashletId,
  conversationId,
  onClose,
  guardRef,
  onBlockPushed,
  blockName,
  dashboardName,
  objectName,
  className,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const reportedBlockRef = useRef({
    dashletId: dashletId ?? null,
    conversationId: conversationId ?? null,
  });
  const [blockSession, dispatchBlockSession, blockSessionRef] =
    useBlockSession();
  const [captureInFlight] = useState(() => new WeakSet<AbortSignal>());

  const dashboardQueryKey = [...DASHBOARD.DASHBOARD, context.dashboardId];
  const refreshRef = useRef<Promise<unknown>>(Promise.resolve());
  const refreshDashboard = () => {
    const refresh = queryClient.invalidateQueries({
      queryKey: dashboardQueryKey,
    });
    refreshRef.current = refresh;
    return refresh;
  };

  const chat = useBuilderChat({
    filesWarning: 'AI block version files could not be loaded',
    onServerMessage: (message, meta) => {
      dispatchBlockSession({ type: 'server', message });
      if (message.type === 'session.ready') void refreshDashboard();
      if (message.type === 'push.result') {
        void refreshDashboard();
        const reported = reportedBlockRef.current;
        if (
          message.dashletId !== reported.dashletId ||
          message.conversationId !== reported.conversationId
        ) {
          reportedBlockRef.current = {
            dashletId: message.dashletId,
            conversationId: message.conversationId,
          };
          onBlockPushed?.(message.dashletId, message.conversationId);
        }
      }
      if (message.type === 'snapshot.restored') void refreshDashboard();
      if (message.type === 'block.capture') {
        handleCaptureRequest(
          message,
          meta,
          (request, { signal, reply }) => {
            void captureDashletForRequest(request.dashletId, {
              signal,
              reply,
              waitForRefresh: () => refreshRef.current,
            });
          },
          captureInFlight
        );
      }
    },
    getStart: () =>
      resolveConnectOptions({
        url: DEFAULT_KICODE_URL,
        context,
        latest: {
          dashletId: blockSessionRef.current.dashletId,
          conversationId: blockSessionRef.current.conversationId,
        },
        initial: { dashletId, conversationId },
      }),
    onConnect: () => dispatchBlockSession({ type: 'connecting' }),
  });

  return (
    <BuilderChatPanel
      className={className}
      chat={chat}
      qaPrefix={QA_PREFIX}
      onClose={onClose}
      guardRef={guardRef}
      profile="block-builder"
      title={blockName ?? t('New block')}
      label={blockName || t('AI panel')}
      placeholder={t('Describe the block')}
      emptyPrompt={t('Describe the block you want to build')}
      extraActions={() =>
        blockSession.dashletId ?? dashletId ? null : (
          <CopyPromptMenu
            target={{
              surface: context.targetType,
              dashboardId: context.dashboardId,
              dashboardName: dashboardName ?? '',
              objectId: context.objectId,
              objectName,
            }}
          />
        )
      }
      renderToolRow={(item) => (
        <BlockToolRow item={item} wsUrl={DEFAULT_KICODE_URL} />
      )}
      groupsTool={groupsBlockTool}
    />
  );
};
